import { useCallback, useRef, useState } from 'react';
import { notifications } from '@mantine/notifications';
import {
  detectGender,
  detectProvider,
  generateNutritionReport,
  type NutritionReport,
  type ParseCompleteMessage,
  type ParseErrorMessage,
  type ParseProgressMessage,
  type TabConfig,
} from '@mdev/common';

type AnalysisState = 'idle' | 'detecting' | 'parsing' | 'analyzing' | 'complete' | 'error';

interface UseGeneticAnalysisOptions {
  buildTabConfigs: () => TabConfig[];
  collectTargetRsids: () => string[];
}

interface UseGeneticAnalysisResult {
  state: AnalysisState;
  progress: number;
  progressMessage: string;
  report: NutritionReport | null;
  analyzeFile: (file: File) => Promise<void>;
  reset: () => void;
}

export function useGeneticAnalysis({
  buildTabConfigs,
  collectTargetRsids,
}: UseGeneticAnalysisOptions): UseGeneticAnalysisResult {
  const [state, setState] = useState<AnalysisState>('idle');
  const [progress, setProgress] = useState(0);
  const [progressMessage, setProgressMessage] = useState('');
  const [report, setReport] = useState<NutritionReport | null>(null);
  const workerRef = useRef<Worker | null>(null);

  const terminateWorker = useCallback(() => {
    workerRef.current?.terminate();
    workerRef.current = null;
  }, []);

  const reset = useCallback(() => {
    setState('idle');
    setProgress(0);
    setProgressMessage('');
    setReport(null);
    terminateWorker();
  }, [terminateWorker]);

  const finalizeReport = useCallback(
    (msg: ParseCompleteMessage, file: File, provider: string) => {
      setState('analyzing');
      setProgressMessage('Analyzing genetic raw data...');

      const gender = detectGender({
        hasYChromosome: msg.data.gender.hasY,
        xChromosomeCount: msg.data.gender.xCount,
      });

      const nutritionReport = generateNutritionReport({
        snps: msg.data.snps,
        gender,
        fileInfo: { fileName: file.name, fileSize: file.size, provider },
        tabConfigs: buildTabConfigs(),
      });

      setReport(nutritionReport);
      setState('complete');
      setProgress(100);

      notifications.show({
        title: 'Analysis Complete',
        message: `Found ${nutritionReport.summary.totalSNPsFound} of ${nutritionReport.summary.totalSNPsAnalyzed} SNPs`,
        color: 'green',
      });

      terminateWorker();
    },
    [buildTabConfigs, terminateWorker],
  );

  const handleParseError = useCallback(
    (msg: ParseErrorMessage) => {
      notifications.show({
        title: 'Parsing Error',
        message: msg.data.error,
        color: 'red',
      });
      setState('error');
      terminateWorker();
    },
    [terminateWorker],
  );

  const analyzeFile = useCallback(
    async (file: File) => {
      try {
        setState('detecting');
        setProgress(0);
        setProgressMessage('Detecting file format...');

        const firstChunk = await readFirstChunk(file);
        const providerInfo = detectProvider({ firstChunk });

        if (providerInfo.provider === 'unknown') {
          notifications.show({
            title: 'Unknown File Format',
            message:
              'Could not detect file format. Please ensure you uploaded a 23andMe or AncestryDNA raw data file.',
            color: 'red',
          });
          setState('error');
          return;
        }

        notifications.show({
          title: 'File Detected',
          message: `Detected ${providerInfo.provider} format with ${providerInfo.confidence} confidence`,
          color: 'blue',
        });

        setState('parsing');
        setProgressMessage('Parsing genetic data...');

        const worker = new Worker(
          new URL('../workers/genetic-file-parser.worker.ts', import.meta.url),
          { type: 'module' },
        );
        workerRef.current = worker;

        worker.onmessage = (event: MessageEvent) => {
          const message = event.data;
          if (message.type === 'progress') {
            const progressMsg = message as ParseProgressMessage;
            setProgress(progressMsg.data.percentComplete);
            setProgressMessage(
              `Processing: ${progressMsg.data.linesProcessed.toLocaleString()} lines, ` +
                `${progressMsg.data.matchesFound} SNPs found`,
            );
          } else if (message.type === 'complete') {
            finalizeReport(message as ParseCompleteMessage, file, providerInfo.provider);
          } else if (message.type === 'error') {
            handleParseError(message as ParseErrorMessage);
          }
        };

        worker.onerror = (error) => {
          console.error('Worker error:', error);
          notifications.show({
            title: 'Worker Error',
            message: 'Failed to create processing worker',
            color: 'red',
          });
        };

        worker.postMessage({
          type: 'start',
          data: {
            file,
            provider: providerInfo.provider,
            targetRsids: collectTargetRsids(),
          },
        });
      } catch (error) {
        console.error('Error processing file:', error);
        notifications.show({
          title: 'Processing Error',
          message: 'An error occurred while processing your file',
          color: 'red',
        });
        setState('error');
      }
    },
    [collectTargetRsids, finalizeReport, handleParseError],
  );

  return { state, progress, progressMessage, report, analyzeFile, reset };
}

const readFirstChunk = (file: File): Promise<string> =>
  new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (e) => resolve(((e.target?.result as string) || '').slice(0, 65536));
    reader.readAsText(file.slice(0, 65536));
  });
